import { Component, ChangeDetectorRef, Renderer2, Output, EventEmitter, OnInit, OnDestroy } from '@angular/core';
import { AttachmentBus } from '../../shared/models/AttachmentBus';
import { QuoteService } from '../../shared/services/quote.service';
import { DomSanitizer} from '@angular/platform-browser';
import { GlobalsService } from '@app/cp/shared/services/globals.service';
import { UploadComponent, FileState, SelectEvent, UploadEvent } from '@progress/kendo-angular-upload';
import { BaseGridEditAttachmentsComponent } from '@app/cp/shared/base-components/base-grid-edit-attachments.component';
import { UntypedFormGroup, UntypedFormControl } from '@angular/forms';
import { DateFunctions } from '@app/cp/shared/static/date-functions';
import { BaseMessageInfo } from '@app/cp/shared/models/BaseMessageInfo';
import { FeatureFlagService } from "@app/cp/shared/services/feature-flag.service";
import { UseFeatureFlagSet, IFeatureFlagSet } from '@app/cp/shared/decorators/featureflags/featureflagset-decorator';
import { SaveMode } from '@app/cp/shared/enums/SaveMode';
import { CommonFunctions } from '@app/cp/shared/static/common-functions';
import { CmProAuthService } from '@app/core/services';
import { nameOf } from '@app/core/utils/type-helpers';
import { AppClaims } from '@app/cp/shared/models/AppClaims';
import { MessageType } from '@app/cp/shared/enums/MessageType';
import { UsersBus } from '@app/cp/shared/models/UsersBus.model';

@UseFeatureFlagSet()
@Component({
    selector: 'quote-attachments',
    templateUrl: './quote-attachments.component.html',
    styleUrls: ['./quote-attachments.component.scss'],
    standalone: false
})
export class QuoteAttachmentsComponent extends BaseGridEditAttachmentsComponent<AttachmentBus, null, QuoteService> implements OnInit, OnDestroy {
    compFeatureFlagSet: IFeatureFlagSet = this as any;
    public static virtualizationSettings = { itemHeight: 30, pageSize: 100 };

    constructor(globals: GlobalsService, renderer: Renderer2, cd: ChangeDetectorRef, sanitizer: DomSanitizer, svc_data: QuoteService, featureFlagService: FeatureFlagService, private authService: CmProAuthService) {
        super(globals, renderer, cd, sanitizer, featureFlagService);
        this.svc_data = svc_data;
        this.className = "AttachmentBus";
        this.appCode = "QuoteAttachments";        

        this.disableCpSearchDialog();
    }

    ngOnInit() {
        this.authService.subscribeToLoginEvents((user: UsersBus) => {
            if (user) {
                this.rights = user.appClaims;
            }
        });
    }

    override ngOnDestroy() {
        super.ngOnDestroy();
        this.authService.unsubscribeToLoginEvents();
    }

    override createForm() {
        this.editForm = new UntypedFormGroup({
            'id': new UntypedFormControl(),
            'referenceId': new UntypedFormControl(),
            'attachmentName': new UntypedFormControl(),
            'attachmentType': new UntypedFormControl(),
            'addedDate': new UntypedFormControl(),
            'personId': new UntypedFormControl(),
            'personName': new UntypedFormControl(),
            'attachmentDesc': new UntypedFormControl(),
            'insertDt': new UntypedFormControl(),
            'insertUserId': new UntypedFormControl(),
            'updateDt': new UntypedFormControl(),
            'updateUserId': new UntypedFormControl(),
            'attachment': new UntypedFormControl()
        });
        
        if(this.compFeatureFlagSet.featureFlagSet["ui-quote-attachments-visibility-to-purchasing-only-evaluation"]) {
            this.editForm.addControl('visibleToPurchasingOnly', new UntypedFormControl());
        }
        else {
            this.editForm.addControl('visibleToCustomer', new UntypedFormControl());
        }
    }
    
    override setForm() {
        this.editForm.patchValue({
            id: this.selectedData.id,
            referenceId: this.isNew ? this._parentId : this.selectedData.referenceId,
            attachmentName: this.selectedData.attachmentName,
            attachmentType: this.selectedData.attachmentType,
            attachment: this.selectedData.attachment,
            addedDate: this.isNew ? this._parentId : this.selectedData.addedDate,
            personId: this.isNew ? this._parentId : this.selectedData.personId,
            personName: this.selectedData.personName,
            attachmentDesc: this.selectedData.attachmentDesc,            
            insertDt: this.selectedData.insertDt,
            insertUserId: this.selectedData.insertUserId,
            updateDt: this.selectedData.updateDt,
            updateUserId: this.selectedData.updateUserId
        });

        if(this.compFeatureFlagSet.featureFlagSet["ui-quote-attachments-visibility-to-purchasing-only-evaluation"]) {
            this.editForm.patchValue({
                visibleToPurchasingOnly: this.selectedData.visibleToPurchasingOnly
            });
        }
        else {
            this.editForm.patchValue({
                visibleToCustomer: this.selectedData.visibleToCustomer
            });
        }
    }

    override setFromReferenceData(data: AttachmentBus) {
        data.personName = this.personName(data.personId);
        return data;
    }

    protected override getChangedObject(data: AttachmentBus) {
        let busObj: AttachmentBus = new AttachmentBus(data);
        busObj.setParentId(this._parentId);
        busObj.setCommon(this.globals.userId, DateFunctions.getDateNow(), this.globals.companyId, this.globals.personId);
        busObj.setBeforeSave();
        return busObj;
    }

    override save(data: AttachmentBus) {
        let busObj: AttachmentBus = this.getChangedObject(data);
        busObj.setParentId(this._parentId);
        busObj.setCommon(this.globals.userId, DateFunctions.getDateNow(), this.globals.companyId, this.globals.personId);

        this.dataLoading = true;

        this.svc_data.uploadAttachment(this.files, busObj).subscribe({
            next: (info: BaseMessageInfo) => {
                if (!info.saveError) {
                    let newBusObj: AttachmentBus = new AttachmentBus(info.primaryReturn);
                    this.saveSuccess(newBusObj);

                    this.svc_data.getAttachments(this._parentId).subscribe(data => {
                        this.gridData = data;
                        this.cd.detectChanges();
                    });
                } else {
                    this.saveError(info.primaryReturn as AttachmentBus, info.message);
                }
                this.clearUpload();
                this.dataLoading = false;
                this.cd.detectChanges();
            },
            error: (error) => {
                this.dataLoading = false;
                this.cd.detectChanges();
            }
        });
    }

    override deleteIndividual(data: AttachmentBus) {
        if (data && data.id) {
            this.svc_data.deleteQuoteAttachment(data.id).subscribe({
                next: (info: BaseMessageInfo) => {
                    if (!info.saveError) {
                        // Instead of calling removeLocal, refresh the grid data
                        this.svc_data.getAttachments(this._parentId).subscribe(gridData => {
                            this.gridData = gridData;
                            this.cd.detectChanges();
                        });
                    } else {
                        this.deleteError(data, info.message);
                    }
                },
                error: (error) => {
                    this.deleteError(data, 'Error deleting attachment');
                }
            });
        } else {
            this.closeEditor();
        }
    }

    protected override removeLocal(data: AttachmentBus): void {
        super.removeLocal(data);
        this.cd.detectChanges();
    }

    override selectEventHandler(e: SelectEvent) {
        super.selectEventHandler(e);
        CommonFunctions.patchEditFormWithAttachment(this.files?.[0], this.editForm);
    }

    public remove(upload: UploadComponent, uid: string) {
        upload.removeFilesByUid(uid);
    }

    public showButton(state: FileState): boolean {
        return (state === FileState.Selected) ? true : false;
    }

    get showId(): boolean {
        return this.editedRowIndex == undefined && this.isNew == false ? true : false;
    }

    override view(data: any) {
        let busObj: AttachmentBus = new AttachmentBus(data);
        super.view(busObj);
    }

    //needed if a user doesn't click into an edit form to update the checkbox
    instantUpdateCBChanged(e: any, dataItem: AttachmentBus, column) {
            if (column.field == "visibleToPurchasingOnly") {
                dataItem.visibleToPurchasingOnly = e.target.checked;
                dataItem.isUpdated = true;
                this.sendParentMessage(MessageType.changed);
            }    
    }
}